/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import Select from "react-select";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
    Row, Col, Card, CardBody, Button, Input, Table, Badge, Pagination, PaginationItem, PaginationLink
} from "reactstrap";

const ManualVehicleExpense = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Data States
    const [vehicleOptions, setVehicleOptions] = useState([]);
    const [expenseList, setExpenseList] = useState([]);
    
    // Pagination & Filter States
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const limit = 20;

    const [filters, setFilters] = useState({
        vehicle_id: null,
        expense_type: null,
        from_date: null,
        to_date: null
    });

    const [expenseForm, setExpenseForm] = useState({
        vehicle_id: null,
        expense_type: { value: "fuel", label: "Fuel" },
        amount: "",
        expense_date: dayjs(),
        remarks: ""
    });

    const expenseOptions = [
        { value: "fuel", label: "Fuel" },
        { value: "parking", label: "Parking" },
        { value: "cash_card", label: "CashCard / ERP" },
        { value: "service", label: "Service" },
        { value: "other", label: "Other" }
    ];

    const fetchVehicles = useCallback(async () => {
        try {
            const res = await Handler({ method: "get", url: "/vehicle/list.php" });
            if (res?.success && res.data) {
                const options = res.data.map(v => ({
                    value: v.id,
                    label: v.vehicle_number ? `${v.vehicle_number} (${v.lorry_type || 'N/A'})` : v.lorry_type
                }));
                setVehicleOptions(options);
            }
        } catch (err) { console.error(err); }
    }, []);

    // ✅ Machan: Updated Load Expenses with Filters & Pagination
    const loadExpenses = useCallback(async (page = 1) => {
        dispatch(loadingTrue());
        try {
            let url = `/expenses/list.php?page=${page}&limit=${limit}`;
            
            if (filters.vehicle_id) url += `&vehicle_id=${filters.vehicle_id.value}`;
            if (filters.expense_type) url += `&expense_type=${filters.expense_type.value}`;
            if (filters.from_date) url += `&from_date=${filters.from_date.format("YYYY-MM-DD")}`;
            if (filters.to_date) url += `&to_date=${filters.to_date.format("YYYY-MM-DD")}`;

            const res = await Handler({ method: "get", url });
            if (res?.success) {
                setExpenseList(res.data || []);
                setTotalPages(res.total_pages || 1);
                setCurrentPage(res.current_page || 1);
                setTotalRecords(res.total_records || 0);
                console.log("Loaded Expenses:", res);
            }
        } catch (err) {
            toast.error("Failed to load expenses");
        }
        dispatch(loadingFalse());
    }, [dispatch, filters]);

    useEffect(() => {
        fetchVehicles();
        loadExpenses(1);
    }, [fetchVehicles, loadExpenses]);

    const handleAddExpense = async (e) => {
        e.preventDefault();
        if (!expenseForm.vehicle_id) return toast.warning("Please select a vehicle");
        
        dispatch(loadingTrue());
        const payload = {
            vehicle_id: expenseForm.vehicle_id.value,
            expense_type: expenseForm.expense_type.value,
            amount: expenseForm.amount,
            expense_date: expenseForm.expense_date.format("YYYY-MM-DD"),
            remarks: expenseForm.remarks
        };

        const res = await Handler({ method: "post", url: "/expenses/add.php", data: payload });
        dispatch(loadingFalse());
        
        if (res?.success || res?.status) {
            toast.success("Expense added successfully!");
            setExpenseForm({ ...expenseForm, amount: "", remarks: "", vehicle_id: null });
            loadExpenses(1); 
        } else {
            toast.error(res?.message || "Failed to add expense");
        }
    };

    const clearFilters = () => {
        setFilters({ vehicle_id: null, expense_type: null, from_date: null, to_date: null });
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="bg-light min-vh-100">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-3 rounded-4 shadow-sm border border-orange-soft">
                    <h4 className="fw-bold text-dark mb-0">Vehicle Expense Tracker</h4>
                    <Button color="dark" outline className="rounded-pill px-4 btn-sm" onClick={() => navigate(-1)}>Back</Button>
                </div>

                {/* Filters Row */}
                <Card className="border-0 shadow-sm rounded-4 mb-4">
                    <CardBody className="p-3">
                        <Row className="align-items-end g-2">
                            <Col md={3}>
                                <label className="extra-small fw-bold text-muted">Filter Vehicle</label>
                                <Select options={vehicleOptions} isClearable value={filters.vehicle_id} onChange={val => setFilters({...filters, vehicle_id: val})} placeholder="All Vehicles" />
                            </Col>
                            <Col md={2}>
                                <label className="extra-small fw-bold text-muted">Category</label>
                                <Select options={expenseOptions} isClearable value={filters.expense_type} onChange={val => setFilters({...filters, expense_type: val})} placeholder="All" />
                            </Col>
                            <Col md={2}>
                                <label className="extra-small fw-bold text-muted">From</label>
                                <DatePicker value={filters.from_date} onChange={val => setFilters({...filters, from_date: val})} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                            </Col>
                            <Col md={2}>
                                <label className="extra-small fw-bold text-muted">To</label>
                                <DatePicker value={filters.to_date} onChange={val => setFilters({...filters, to_date: val})} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                            </Col>
                            <Col md={3} className="d-flex gap-2">
                                <Button color="primary" className="flex-grow-1" onClick={() => loadExpenses(1)}>Apply</Button>
                                <Button color="secondary" outline onClick={clearFilters}>Clear</Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                <Row>
                    {/* Add Form */}
                    <Col lg={4}>
                        <Card className="border-0 shadow-sm rounded-4 position-sticky" style={{ top: '20px' }}>
                            <CardBody className="p-4">
                                <h5 className="fw-bold mb-4">Record Expense</h5>
                                <form onSubmit={handleAddExpense}>
                                    <div className="mb-3">
                                        <label className="small fw-bold text-muted">Vehicle Selection</label>
                                        <Select options={vehicleOptions} value={expenseForm.vehicle_id} onChange={val => setExpenseForm({ ...expenseForm, vehicle_id: val })} placeholder="Search..." isSearchable />
                                    </div>
                                    <div className="mb-3">
                                        <label className="small fw-bold text-muted">Category</label>
                                        <Select options={expenseOptions} value={expenseForm.expense_type} onChange={val => setExpenseForm({ ...expenseForm, expense_type: val })} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="small fw-bold text-muted">Amount ($)</label>
                                        <Input type="number" required value={expenseForm.amount} onChange={e => setExpenseForm({ ...expenseForm, amount: e.target.value })} />
                                    </div>
                                    <div className="mb-3">
                                        <label className="small fw-bold text-muted">Date</label>
                                        <DatePicker value={expenseForm.expense_date} onChange={val => setExpenseForm({ ...expenseForm, expense_date: val })} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                                    </div>
                                    <Button color="primary" block className="rounded-3 py-2 fw-bold shadow-sm">SAVE RECORD</Button>
                                </form>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* List Table */}
                    <Col lg={8}>
                        <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                            <div className="bg-white border-bottom p-3 d-flex justify-content-between align-items-center">
                                <h6 className="fw-bold mb-0">Expense History</h6>
                                <Badge color="soft-primary" className="text-primary rounded-pill px-3">Records: {totalRecords}</Badge>
                            </div>
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="px-3">Vehicle</th>
                                            <th>Type</th>
                                            <th>Date</th>
                                            <th className="text-end px-3">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {expenseList.length > 0 ? expenseList.map(ex => (
                                            <tr key={ex.id}>
                                                <td className="px-3 fw-bold">{ex.vehicle_number}</td>
                                                <td><Badge color="soft-info" className="text-info bg-info bg-opacity-10">{ex.expense_type}</Badge></td>
                                                <td className="small text-muted">{dayjs(ex.expense_date).format("DD MMM YYYY")}</td>
                                                <td className="text-end px-3 fw-bold text-danger">- ${parseFloat(ex.amount).toLocaleString('en-IN')}</td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="4" className="text-center py-5 text-muted">No data found.</td></tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination Controls */}
                            {totalPages > 1 && (
                                <div className="p-3 d-flex justify-content-center border-top">
                                    <Pagination size="sm">
                                        <PaginationItem disabled={currentPage <= 1}>
                                            <PaginationLink previous onClick={() => loadExpenses(currentPage - 1)} />
                                        </PaginationItem>
                                        {[...Array(totalPages)].map((_, idx) => (
                                            <PaginationItem active={idx + 1 === currentPage} key={idx}>
                                                <PaginationLink onClick={() => loadExpenses(idx + 1)}>{idx + 1}</PaginationLink>
                                            </PaginationItem>
                                        ))}
                                        <PaginationItem disabled={currentPage >= totalPages}>
                                            <PaginationLink next onClick={() => loadExpenses(currentPage + 1)} />
                                        </PaginationItem>
                                    </Pagination>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            </div>
        </LocalizationProvider>
    );
};

export default ManualVehicleExpense;